import { gql } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { PartialDeep } from 'type-fest';

import { CustodianProfileDocument } from 'src/graphql/actions/custodian/custodianProfile.query';
import { CUSTODIAN_PROFILES_LIMIT } from 'src/shared/constants';

import { apolloClient as client, apolloCache as cache } from '../apollo-client';

import type { ICustodianOperation, ICustodianProfile } from 'src/graphql/schema';
import type { ICustodianProfileQuery } from 'src/graphql/actions/custodian/custodianProfile.query';
import type { ICustodianProfilesQuery } from 'src/graphql/actions/custodian/custodianProfiles.query';

const CustodianOperationFragment = gql`
  fragment CustodianOperationFragment on CustodianProfile {
    custodianOperations {
      id
      custodianServiceEvents
      custodianServiceEventsCustom
      dataRetention
      endOfRetentionPeriod
      endOfRetentionPeriodCustom
      operationType
      operationTypeCustom
      regulatoryRequirements
      regulatoryRequirementsCustom
    }
  }
`;

export const removeCustodianOperationCache = (profileId: string, operationId: string) => {
  const cacheData = cloneDeep<ICustodianProfileQuery | null>(
    client.readQuery({
      query: CustodianProfileDocument,
      variables: {
        id: profileId,
      },
    }),
  );

  if (!cacheData?.custodianProfile) return;

  const custodianOperations = cacheData.custodianProfile.custodianOperations.filter(({ id }) => id !== operationId);

  cache.writeQuery({
    query: CustodianProfileDocument,
    variables: {
      id: profileId,
    },
    data: {
      custodianProfile: {
        ...cacheData.custodianProfile,
        custodianOperations,
        policy: {
          ...cacheData.custodianProfile.policy,
          submitForReview: Boolean(custodianOperations.length),
        },
      },
    },
  });
};

export const updateCustodianOperationCache = (operationId: string, data?: PartialDeep<ICustodianOperation>) => {
  if (!data) return;

  client.writeFragment({
    id: `CustodianOperation:${operationId}`,
    fragment: CustodianOperationFragment,
    data,
  });
};

export const addCustodianOperationCache = (profileId: string, data: ICustodianOperation) => {
  const cacheData = cloneDeep<ICustodianProfileQuery | null>(
    client.readQuery({
      query: CustodianProfileDocument,
      variables: {
        id: profileId,
      },
    }),
  );

  if (!cacheData?.custodianProfile) return;

  const custodianOperations = [...cacheData.custodianProfile.custodianOperations, data];

  client.writeQuery({
    query: CustodianProfileDocument,
    variables: {
      id: profileId,
    },
    data: {
      custodianProfile: {
        ...cacheData.custodianProfile,
        custodianOperations,
        policy: {
          ...cacheData.custodianProfile.policy,
          submitForReview: true,
        },
      },
    },
  });
};

export const addCustodianProfileCache = (data: ICustodianProfile) => {
  client.cache.modify({
    fields: {
      custodianProfiles: (existingData) => ({
        ...existingData,
        nodes: [data, ...existingData.nodes],
        nodesCount: existingData.nodesCount + 1,
        pageCount: Math.ceil((existingData.nodesCount + 1) / CUSTODIAN_PROFILES_LIMIT),
      }),
    },
  });
};

export const removeCustodianProfileCache = (profileId: string) => {
  cache.modify({
    fields: {
      custodianProfiles: (existingData, { readField }) => ({
        ...existingData,
        nodes: existingData.nodes.filter(
          (node: ICustodianProfilesQuery['custodianProfiles']['nodes'][number]) => readField('id', node) !== profileId,
        ),
        nodesCount: existingData.nodesCount - 1,
        pageCount: Math.ceil((existingData.nodesCount - 1) / CUSTODIAN_PROFILES_LIMIT),
      }),
    },
  });
};
